/**
 * React hook for real-time updates stream
 * Story 234: Real-time MCP Updates Stream
 *
 * Manages WebWorker for persistent WebSocket connection.
 * Provides events and connection status.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { config } from '../config';
import type { WorkerCommand, WorkerMessage } from '../workers/updates.worker';

/** Entity types that can generate events */
type EntityType = 'story' | 'task' | 'document' | 'document_section' | 'comment' | 'epic' | 'project';

/** Operations that can be performed on entities */
type Operation = 'create' | 'update' | 'delete' | 'start' | 'complete' | 'settings_changed';

/** Tab types for navigation */
type TabType = 'tasks' | 'prd' | 'design' | 'overview';

/** Source of the event */
type ActorSource = 'mcp' | 'web' | 'api';

/** Navigation target for clicking on an update entry */
interface NavigationTarget {
  tab: TabType;
  entityId?: string;
}

/** MCP event from real-time stream */
export interface McpStreamEvent {
  id: string;
  timestamp: string;
  projectId: string;
  storyId: string | null;
  entityType: EntityType;
  operation: Operation;
  summary: string;
  navigationTarget: NavigationTarget;
  actorUserId: string | null;
  actorSource: ActorSource;
  details?: Record<string, unknown>;
}

/** Connection status states */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Maximum number of events to keep in memory */
const MAX_EVENTS = 100;

/** LocalStorage key for persisted events */
const STORAGE_KEY_PREFIX = 'ceetrix_updates_';

/** Load events from localStorage */
function loadPersistedEvents(projectId: string): McpStreamEvent[] {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${projectId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[useUpdatesStream] Failed to load persisted events:', e);
  }
  return [];
}

/** Save events to localStorage */
function persistEvents(projectId: string, events: McpStreamEvent[]): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${projectId}`, JSON.stringify(events));
  } catch (e) {
    console.warn('[useUpdatesStream] Failed to persist events:', e);
  }
}

/** Hook return type */
export interface UseUpdatesStreamReturn {
  /** List of recent events (most recent first) */
  events: McpStreamEvent[];
  /** Current connection status */
  connectionStatus: ConnectionStatus;
  /** Current subscribed project ID */
  subscribedProjectId: string | null;
  /** Last error message (e.g., subscription failure) */
  lastError: string | null;
  /** Clear all events */
  clearEvents: () => void;
  /** Manually reconnect */
  reconnect: () => void;
}

/** Options for useUpdatesStream hook */
export interface UseUpdatesStreamOptions {
  /** Callback when a new event is received (useful for cache invalidation) */
  onEvent?: (event: McpStreamEvent) => void;
}

/**
 * Hook for managing real-time updates stream
 *
 * @param projectId - Project ID to subscribe to (null to not subscribe)
 * @param options - Optional configuration
 * @returns Stream state and control functions
 */
export function useUpdatesStream(
  projectId: string | null,
  options?: UseUpdatesStreamOptions
): UseUpdatesStreamReturn {
  // Initialize events from localStorage
  const [events, setEvents] = useState<McpStreamEvent[]>(() => {
    if (projectId) {
      return loadPersistedEvents(projectId);
    }
    return [];
  });
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [subscribedProjectId, setSubscribedProjectId] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const onEventRef = useRef(options?.onEvent);
  const projectIdRef = useRef(projectId);

  // Keep refs updated
  onEventRef.current = options?.onEvent;
  projectIdRef.current = projectId;

  // Create and manage WebWorker
  useEffect(() => {
    // Create worker using Vite's worker import syntax
    const worker = new Worker(
      new URL('../workers/updates.worker.ts', import.meta.url),
      { type: 'module' }
    );

    workerRef.current = worker;

    // Handle messages from worker
    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const message = event.data;

      switch (message.type) {
        case 'connected':
          setConnectionStatus('connected');
          break;

        case 'disconnected':
          setConnectionStatus('disconnected');
          break;

        case 'reconnecting':
          setConnectionStatus('reconnecting');
          break;

        case 'event':
          setEvents((prev) => {
            const newEvents = [message.payload, ...prev].slice(0, MAX_EVENTS);
            return newEvents;
          });
          // Call onEvent callback for cache invalidation
          if (onEventRef.current) {
            onEventRef.current(message.payload);
          }
          break;

        case 'subscribed':
          setSubscribedProjectId(message.projectId);
          setLastError(null); // Clear any previous error
          break;

        case 'unsubscribed':
          if (subscribedProjectId === message.projectId) {
            setSubscribedProjectId(null);
          }
          break;

        case 'error':
          setLastError(message.message);
          break;
      }
    };

    // Connect to WebSocket
    setConnectionStatus('connecting');
    const command: WorkerCommand = { type: 'connect', apiUrl: config.apiUrl };
    worker.postMessage(command);

    // Cleanup on unmount
    return () => {
      const disconnectCommand: WorkerCommand = { type: 'disconnect' };
      worker.postMessage(disconnectCommand);
      worker.terminate();
      workerRef.current = null;
    };
  }, []); // Only run once on mount

  // Subscribe/unsubscribe when projectId changes
  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;

    // Unsubscribe from previous project
    if (subscribedProjectId && subscribedProjectId !== projectId) {
      const command: WorkerCommand = { type: 'unsubscribe', projectId: subscribedProjectId };
      worker.postMessage(command);
    }

    // Subscribe to new project
    if (projectId && projectId !== subscribedProjectId) {
      const command: WorkerCommand = { type: 'subscribe', projectId };
      worker.postMessage(command);
    }
  }, [projectId, subscribedProjectId]);

  // Persist events to localStorage when they change
  useEffect(() => {
    if (projectIdRef.current) {
      persistEvents(projectIdRef.current, events);
    }
  }, [events]);

  // Clear all events
  const clearEvents = useCallback(() => {
    setEvents([]);
    if (projectIdRef.current) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${projectIdRef.current}`);
    }
  }, []);

  // Manually reconnect
  const reconnect = useCallback(() => {
    const worker = workerRef.current;
    if (!worker) return;

    // Disconnect and reconnect
    const disconnectCommand: WorkerCommand = { type: 'disconnect' };
    worker.postMessage(disconnectCommand);

    setTimeout(() => {
      setConnectionStatus('connecting');
      const connectCommand: WorkerCommand = { type: 'connect', apiUrl: config.apiUrl };
      worker.postMessage(connectCommand);
    }, 100);
  }, []);

  return {
    events,
    connectionStatus,
    subscribedProjectId,
    lastError,
    clearEvents,
    reconnect,
  };
}
